import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2, Trophy, AlarmClock, DoorOpen, UserX, Zap, Truck, TrendingUp, TrendingDown, Minus, FileDown, LifeBuoy, ArrowLeft, HardHat, Camera, StickyNote } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { api, API } from "../../lib/api";

const C = { done: "#0f766e", prog: "#0284c7", pend: "#b8a06a", ore: "#1c1c1c", straord: "#d97706", assenze: "#e11d48" };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function shiftMonth(m, delta) {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y, mm - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const monthLabel = (m) => `${MESI[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const fmtMin = (v) => `${Math.floor((v || 0) / 60)}h ${String((v || 0) % 60).padStart(2, "0")}m`;

const ChartTip = ({ active, payload, label, fmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-ink text-cream text-xs px-3 py-2 rounded-sm shadow-lg">
      <div className="font-bold mb-0.5">{label}</div>
      {payload.map((p, i) => <div key={i}>{fmt ? fmt(p.value, p.payload) : p.value}</div>)}
    </div>
  );
};

const Card = ({ title, icon: Icon, children, testid, subtitle }) => (
  <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={15} className="text-slate1" />
      <h3 className="text-[11px] tracking-[0.2em] uppercase font-bold text-ink">{title}</h3>
    </div>
    {subtitle && <p className="text-[11px] text-slate1 mb-3">{subtitle}</p>}
    {children}
  </div>
);

const StatusDonut = ({ title, icon, testid, subtitle, counts, emptyLabel, onSlice }) => {
  const pie = [
    { name: "Completate", status: "completed", value: counts.completed || 0, pct: counts.pct_completed || 0, color: C.done },
    { name: "In corso (parziali)", status: "in_progress", value: counts.in_progress || 0, pct: counts.pct_partial || 0, color: C.prog },
    { name: "Da fare", status: "pending", value: counts.pending || 0, pct: counts.pct_pending || 0, color: C.pend },
  ];
  const filled = pie.filter((s) => s.value > 0);
  return (
    <Card title={title} icon={icon} testid={testid} subtitle={subtitle}>
      {counts.total ? (
        <div className="flex items-center gap-4">
          <div className="relative w-[190px] h-[190px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={filled} dataKey="value" innerRadius={58} outerRadius={88} paddingAngle={3} strokeWidth={0}
                  style={{ cursor: "pointer" }}
                  onClick={(entry) => { const p = entry?.payload || entry; if (p?.status) onSlice(p.status, p.name); }}
                >
                  {filled.map((s) => <Cell key={s.name} fill={s.color} />)}
                </Pie>
                <Tooltip content={<ChartTip fmt={(v, p) => `${p.name}: ${v} — clicca per la lista`} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="font-display text-3xl text-teal-700">{counts.pct_completed || 0}%</span>
              <span className="text-[9px] tracking-[0.18em] uppercase text-slate1">completate</span>
            </div>
          </div>
          <div className="space-y-1 text-sm">
            {pie.map((s) => (
              <button
                key={s.name} type="button" onClick={() => onSlice(s.status, s.name)}
                className="flex items-center gap-2 hover:bg-cream2 rounded-sm px-1.5 py-1 -mx-1.5 transition-colors w-full text-left"
                title="Clicca per vedere la lista"
                data-testid={`${testid}-legend-${s.status}`}
              >
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-ink">{s.name}</span>
                <b className="ml-1">{s.value}</b>
                <span className="text-slate1 text-xs">({s.pct}%)</span>
              </button>
            ))}
          </div>
        </div>
      ) : <div className="py-12 text-center text-sm text-slate-400">{emptyLabel}</div>}
    </Card>
  );
};

const DRILL_STATUS_CHIP = {
  pending: "bg-white border-ink/15 text-ink",
  in_progress: "bg-sky-50 border-sky-300 text-sky-900",
  completed: "bg-emerald-50 border-emerald-300 text-emerald-900",
};
const DRILL_STATUS_LABEL = { pending: "Da fare", in_progress: "In corso", completed: "Completata" };

const DrillPanel = ({ drill, month, onClose, onOpenTask }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/installers/admin/stats/month/tasks", {
          params: { month, status: drill.status, task_type: drill.task_type },
        });
        setRows(data);
      } catch { setRows([]); }
    })();
  }, [drill, month]);
  const isAssist = drill.task_type === "assistance";
  return (
    <div className="fixed inset-0 z-[9400] bg-cream flex flex-col" data-testid="stats-drill-page">
      <div className="bg-white border-b border-ink/10 px-5 py-4 flex items-center gap-3 shrink-0">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/15 rounded-sm text-[11px] tracking-wider uppercase text-slate1 hover:text-ink" data-testid="stats-drill-back">
          <ArrowLeft size={13} /> Statistiche
        </button>
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">{isAssist ? "Assistenze" : "Consegne"} · {monthLabel(month)}</div>
          <div className="font-display text-lg text-ink leading-tight">{drill.label}</div>
        </div>
        {rows && <span className="ml-auto text-sm text-slate1" data-testid="stats-drill-count"><b className="text-ink">{rows.length}</b> {isAssist ? "assistenze" : "consegne"}</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-2 max-w-4xl w-full mx-auto">
        {!rows ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-400" data-testid="stats-drill-empty">Nessun risultato</div>
        ) : rows.map((t) => (
          <button
            key={t.id} type="button" onClick={() => onOpenTask && onOpenTask(t)}
            className={`w-full text-left bg-white border border-ink/10 rounded-sm p-3.5 hover:border-ink/30 transition-[border-color] ${t.task_type === "assistance" ? "border-l-4 border-l-violet-600" : ""}`}
            data-testid={`stats-drill-task-${t.id}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-ink">
                {t.scheduled_date
                  ? new Date(`${t.scheduled_date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" })
                  : "Non pianificata"}
              </span>
              {t.scheduled_time && <span className="text-[11px] text-slate1">ore {t.scheduled_time}</span>}
              <span className="font-medium text-ink">{t.title}</span>
              {t.task_type === "assistance" && <span className="text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-violet-50 border-violet-300 text-violet-900">Assistenza</span>}
              <span className={`text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${DRILL_STATUS_CHIP[t.status]}`}>{DRILL_STATUS_LABEL[t.status]}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-slate-600">
              {t.client_address && <span>{t.client_address}</span>}
              {(t.workers || []).length > 0 && <span className="inline-flex items-center gap-1"><HardHat size={11} className="text-slate-400" /> {t.workers.map((w) => w.name).join(", ")}</span>}
              {t.photo_count > 0 && <span className="inline-flex items-center gap-1"><Camera size={11} className="text-slate-400" /> {t.photo_count} foto</span>}
              {t.client_delivery_notes && <span className="inline-flex items-center gap-1 text-amber-800"><StickyNote size={11} className="text-amber-500" /> <span className="truncate max-w-[240px]">{t.client_delivery_notes}</span></span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const InstallerStats = ({ onOpenTask }) => {
  const [month, setMonth] = useState(() => new Date().toLocaleDateString("sv-SE").slice(0, 7));
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: d } = await api.get("/installers/admin/stats/month", { params: { month } });
      setData(d);
    } catch {
      setData({ error: true });
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>;

  const del = data.deliveries || {};
  const ass = data.assistances || {};
  const workers = data.workers || [];
  const active = workers.filter((w) => w.total_minutes > 0);
  const oreData = [...workers].sort((a, b) => b.total_minutes - a.total_minutes)
    .map((w) => ({ name: w.name, full: w.name, min: w.total_minutes, days: w.days_worked, half: w.half_days }));
  const straordData = [...workers].sort((a, b) => b.overtime_minutes - a.overtime_minutes)
    .map((w) => ({ name: w.name, full: w.name, min: w.overtime_minutes }));
  const assenzeData = [...workers].sort((a, b) => b.absence_days - a.absence_days)
    .map((w) => ({ name: w.name, full: w.name, gg: w.absence_days, rate: w.absence_rate_pct }));
  const withTimes = active.filter((w) => w.avg_in_minutes != null);
  const lateArriver = withTimes.length ? withTimes.reduce((a, b) => (b.avg_in_minutes > a.avg_in_minutes ? b : a)) : null;
  const earlyLeaver = (() => {
    const c = active.filter((w) => w.avg_out_minutes != null);
    return c.length ? c.reduce((a, b) => (b.avg_out_minutes < a.avg_out_minutes ? b : a)) : null;
  })();
  const avgAbsence = workers.length ? (workers.reduce((s, w) => s + w.absence_rate_pct, 0) / workers.length).toFixed(1) : 0;
  const totStraord = workers.reduce((s, w) => s + w.overtime_minutes, 0);
  const BAND_MIN = 6 * 60, BAND_MAX = 21 * 60;
  const pctOf = (v) => Math.min(100, Math.max(0, ((v - BAND_MIN) * 100) / (BAND_MAX - BAND_MIN)));

  return (
    <div className="space-y-4" data-testid="installers-stats">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center bg-white border border-ink/10 rounded-sm">
          <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-2.5 text-slate1 hover:text-ink" data-testid="stats-month-prev"><ChevronLeft size={16} /></button>
          <span className="px-2 text-sm font-medium text-ink min-w-[150px] text-center capitalize" data-testid="stats-month-label">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-2.5 text-slate1 hover:text-ink" data-testid="stats-month-next"><ChevronRight size={16} /></button>
        </div>
        <button onClick={() => setMonth(new Date().toLocaleDateString("sv-SE").slice(0, 7))} className="px-3 py-2.5 bg-white border border-ink/10 rounded-sm text-[11px] tracking-wider uppercase text-slate1 hover:text-ink" data-testid="stats-month-today">Mese corrente</button>
        <a
          href={`${API}/installers/admin/stats/month/pdf?month=${month}&token=${localStorage.getItem("ah_token") || ""}`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2.5 bg-ink text-cream rounded-sm text-[11px] tracking-wider uppercase font-bold"
          data-testid="stats-pdf-btn"
        >
          <FileDown size={13} /> Report PDF
        </a>
        <span className="text-[11px] text-slate1">Assenze calcolate sui <b>{data.working_days_elapsed}</b> giorni feriali (lun–ven) trascorsi del mese.</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(() => {
          const prev = data.previous;
          const curAbs = data.aggregates?.avg_absence_rate_pct ?? Number(avgAbsence);
          const delta = (cur, prv, goodUp, fmt) => {
            if (prv == null || cur == null || !prev) return null;
            const d = Math.round((cur - prv) * 10) / 10;
            if (!d) return <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 mt-1"><Minus size={10} /> uguale a {prev.label.toLowerCase()}</span>;
            const up = d > 0;
            const good = up === goodUp;
            return (
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold mt-1 ${good ? "text-teal-700" : "text-rose-600"}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {up ? "+" : "−"}{fmt(Math.abs(d))} vs {prev.label.toLowerCase()}
              </span>
            );
          };
          const pt = (v) => `${v} pt`;
          return [
            { l: "Consegne completate", v: `${del.pct_completed || 0}%`, s: `${del.completed || 0} su ${del.total || 0}`, c: "text-teal-700", t: "stats-kpi-completed", d: delta(del.pct_completed, prev?.pct_completed, true, pt) },
            { l: "Parziali (in corso)", v: `${del.pct_partial || 0}%`, s: `${del.in_progress || 0} consegne`, c: "text-sky-700", t: "stats-kpi-partial", d: delta(del.pct_partial, prev?.pct_partial, false, pt) },
            { l: "Tasso di assenza medio", v: `${curAbs}%`, s: "media squadra", c: "text-rose-700", t: "stats-kpi-absence", d: delta(curAbs, prev?.avg_absence_rate_pct, false, pt) },
            { l: "Straordinari totali", v: fmtMin(totStraord), s: "oltre 9h/giorno", c: "text-amber-700", t: "stats-kpi-overtime", d: delta(totStraord, prev?.overtime_minutes_total, false, fmtMin) },
          ].map((k) => (
            <div key={k.t} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={k.t}>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">{k.l}</div>
              <div className={`font-display text-3xl mt-1 ${k.c}`}>{k.v}</div>
              <div className="text-[11px] text-slate1 mt-0.5">{k.s}</div>
              <div data-testid={`${k.t}-delta`}>{k.d}</div>
            </div>
          ));
        })()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <StatusDonut
          title="Consegne del mese" icon={Truck} testid="stats-chart-deliveries"
          subtitle={`${del.total || 0} consegne programmate a ${monthLabel(month).toLowerCase()} · clicca una fetta per la lista`}
          counts={del} emptyLabel="Nessuna consegna programmata nel mese"
          onSlice={(status, label) => setDrill({ task_type: "delivery", status, label })}
        />

        <StatusDonut
          title="Assistenze del mese" icon={LifeBuoy} testid="stats-chart-assistances"
          subtitle={`${ass.total || 0} assistenze a ${monthLabel(month).toLowerCase()}${ass.unplanned ? ` (di cui ${ass.unplanned} non pianificate)` : ""} · clicca una fetta per la lista`}
          counts={ass} emptyLabel="Nessuna assistenza nel mese"
          onSlice={(status, label) => setDrill({ task_type: "assistance", status, label })}
        />

        <Card title="Classifica ore lavorate" icon={Trophy} testid="stats-chart-hours" subtitle="Chi ha lavorato di più questo mese">
          <ResponsiveContainer width="100%" height={Math.max(150, oreData.length * 46)}>
            <BarChart data={oreData} layout="vertical" margin={{ left: 8, right: 60 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#1c1c1c" }} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={<ChartTip fmt={(v, p) => `${p.full}: ${fmtMin(v)}`} />} />
              <Bar dataKey="min" fill={C.ore} radius={[0, 3, 3, 0]} barSize={20}>
                <LabelList dataKey="min" position="right" formatter={(v) => fmtMin(v)} style={{ fontSize: 11, fill: "#555" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Straordinari" icon={Zap} testid="stats-chart-overtime" subtitle="Chi fa più straordinari (oltre 9h/giorno)">
          <ResponsiveContainer width="100%" height={Math.max(150, straordData.length * 46)}>
            <BarChart data={straordData} layout="vertical" margin={{ left: 8, right: 60 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#1c1c1c" }} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={<ChartTip fmt={(v, p) => `${p.full}: ${fmtMin(v)}`} />} />
              <Bar dataKey="min" fill={C.straord} radius={[0, 3, 3, 0]} barSize={20}>
                <LabelList dataKey="min" position="right" formatter={(v) => (v ? fmtMin(v) : "—")} style={{ fontSize: 11, fill: "#555" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Giorni di assenza" icon={UserX} testid="stats-chart-absence" subtitle="Giorni feriali senza timbrature (tasso di assenza)">
          <ResponsiveContainer width="100%" height={Math.max(150, assenzeData.length * 46)}>
            <BarChart data={assenzeData} layout="vertical" margin={{ left: 8, right: 76 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#1c1c1c" }} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={<ChartTip fmt={(v, p) => `${p.full}: ${v} gg (${p.rate}%)`} />} />
              <Bar dataKey="gg" fill={C.assenze} radius={[0, 3, 3, 0]} barSize={20}>
                <LabelList dataKey="gg" position="right" formatter={(v) => `${v} gg`} style={{ fontSize: 11, fill: "#555" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Orari medi: arrivo → uscita" icon={AlarmClock} testid="stats-chart-times" subtitle="Media della prima entrata e dell'ultima uscita (escluse le uscite automatiche delle 17:00)">
        {withTimes.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">Nessuna timbratura nel mese</div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between text-[10px] text-slate1 font-mono px-[92px]">
              {["06", "08", "10", "12", "14", "16", "18", "20"].map((h) => <span key={h}>{h}:00</span>)}
            </div>
            {withTimes.map((w) => (
              <div key={w.worker_id} className="flex items-center gap-3" data-testid={`stats-timeband-${w.worker_id}`}>
                <span className="w-32 text-xs text-ink truncate shrink-0" title={w.name}>{w.name}</span>
                <div className="relative flex-1 h-8 bg-cream2 rounded-sm overflow-hidden">
                  {w.avg_out_minutes != null && (
                    <div className="absolute top-1 bottom-1 bg-ink/80 rounded-sm" style={{ left: `${pctOf(w.avg_in_minutes)}%`, width: `${Math.max(1.5, pctOf(w.avg_out_minutes) - pctOf(w.avg_in_minutes))}%` }} />
                  )}
                  <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold text-cream" style={{ left: `calc(${pctOf(w.avg_in_minutes)}% + 6px)` }}>{w.avg_in}</span>
                  {w.avg_out_minutes != null && (
                    <span className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold text-ink" style={{ left: `calc(${pctOf(w.avg_out_minutes)}% + 6px)` }}>{w.avg_out}</span>
                  )}
                </div>
                <div className="w-40 shrink-0 flex gap-1.5">
                  {lateArriver && lateArriver.worker_id === w.worker_id && withTimes.length > 1 && (
                    <span className="text-[9px] tracking-wider uppercase font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid="stats-late-badge"><AlarmClock size={8} className="inline mr-0.5" />arriva più tardi</span>
                  )}
                  {earlyLeaver && earlyLeaver.worker_id === w.worker_id && withTimes.length > 1 && (
                    <span className="text-[9px] tracking-wider uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="stats-early-badge"><DoorOpen size={8} className="inline mr-0.5" />va via prima</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {drill && <DrillPanel drill={drill} month={month} onClose={() => setDrill(null)} onOpenTask={onOpenTask} />}
    </div>
  );
};

export default InstallerStats;
